import { useEffect, useState } from "react";

import { Chevron } from "@/components/ui/Chevron";
import { explainStep, submitRecommendation } from "@/lib/api/sessions";
import type { FitAssessment, RecommendationReadyPayload } from "@/lib/api/types";

/**
 * Recommendation panel.
 *
 * Headline: winner card with score, confidence, justification, pros, cons.
 * Underneath: runner-ups in muted cards so the user can see what we
 * compared against. Footer: accept / reject buttons.
 */
export function RecommendationPanel({
  sessionId,
  recommendation,
}: {
  sessionId: string;
  recommendation: RecommendationReadyPayload;
}) {
  const [submitting, setSubmitting] = useState<"accept" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explanationLoading, setExplanationLoading] = useState(true);

  const winnerFit = recommendation.fits[recommendation.winner];

  // Step 3: fetch the LLM commentary for the recommendation.
  useEffect(() => {
    const controller = new AbortController();
    setExplanationLoading(true);
    setExplanation(null);
    explainStep(sessionId, { step: "recommendation" }, controller.signal)
      .then((r) => setExplanation(r.text))
      .catch(() => setExplanation(null))
      .finally(() => setExplanationLoading(false));
    return () => controller.abort();
  }, [sessionId]);

  async function send(decision: "accept" | "reject") {
    setSubmitting(decision);
    setError(null);
    try {
      await submitRecommendation(sessionId, { decision });
      // Next state update arrives over SSE; nothing more to do here.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed.");
      setSubmitting(null);
    }
  }

  return (
    <section className="space-y-6">
      <header>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <Chevron className="mr-2" /> recommendation
        </p>
        <h2 className="text-2xl font-semibold mt-1">
          {recommendation.winner}
          <span className="text-muted-foreground font-normal ml-2 text-base">
            ({recommendation.confidence} confidence)
          </span>
        </h2>
        {recommendation.rationale ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {recommendation.rationale}
          </p>
        ) : null}
      </header>

      {/* Step 3: LLM commentary panel — grounded in the user's scenario + all fits. */}
      <aside className="rounded-md border border-primary/30 bg-primary/5 p-4">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
          <Chevron className="mr-2" /> why this pattern
        </p>
        {explanationLoading ? (
          <p className="text-sm text-muted-foreground italic">
            Generating explanation…
          </p>
        ) : explanation ? (
          <p className="text-sm leading-relaxed">{explanation}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            (Couldn't generate an explanation right now.)
          </p>
        )}
      </aside>

      {winnerFit ? <FitCard fit={winnerFit} highlight /> : null}

      {recommendation.runner_ups.length > 0 ? (
        <section className="space-y-3">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            <Chevron className="mr-2" /> runners-up
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {recommendation.runner_ups.map((f) => (
              <FitCard key={f.pattern_name} fit={f} />
            ))}
          </div>
        </section>
      ) : null}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <footer className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
        <button
          type="button"
          onClick={() => void send("reject")}
          disabled={submitting !== null}
          className="rounded-md border border-border bg-transparent px-4 py-2 text-sm font-medium hover:bg-secondary/60 disabled:opacity-50 transition-colors"
        >
          {submitting === "reject" ? "Cancelling…" : "Reject"}
        </button>
        <button
          type="button"
          onClick={() => void send("accept")}
          disabled={submitting !== null}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {submitting === "accept" ? "Accepting…" : "Accept recommendation"}
        </button>
      </footer>
    </section>
  );
}

function FitCard({ fit, highlight }: { fit: FitAssessment; highlight?: boolean }) {
  return (
    <article
      className={
        highlight
          ? "rounded-lg border-2 border-primary/40 bg-primary/5 p-5"
          : "rounded-lg border border-border/60 bg-card p-5"
      }
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="text-lg font-semibold">{fit.pattern_name}</h3>
        <span className="font-mono text-sm">
          <span className="text-foreground">{fit.score}</span>
          <span className="text-muted-foreground">/100</span>
        </span>
      </header>
      {fit.justification ? (
        <p className="mt-2 text-sm text-muted-foreground">{fit.justification}</p>
      ) : null}
      {fit.pros.length > 0 ? (
        <ul className="mt-3 space-y-1 text-sm">
          {fit.pros.map((p, i) => (
            <li key={`pro-${i}`} className="text-emerald-500/90">
              + {p}
            </li>
          ))}
        </ul>
      ) : null}
      {fit.cons.length > 0 ? (
        <ul className="mt-1 space-y-1 text-sm">
          {fit.cons.map((c, i) => (
            <li key={`con-${i}`} className="text-amber-500/90">
              − {c}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
